import "@/App.css";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Phone, Clock, Star, Instagram, Facebook, Trophy } from "lucide-react";

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_platinum-barbers/artifacts/llk1xamb_logo_logonew.png";
const HERO_BG_IMAGES = [
  "https://customer-assets.emergentagent.com/job_platinum-barbers/artifacts/lz6qmpgd_IMG_6527.jpeg",
  "https://customer-assets.emergentagent.com/job_platinum-barbers/artifacts/f0j2k3z9_IMG_6526.jpeg"
];
const HAIRCUT_IMAGES = [
"https://customer-assets.emergentagent.com/job_platinum-barbers/artifacts/vui351nn_5.jpg",
"https://customer-assets.emergentagent.com/job_platinum-barbers/artifacts/f7tiwxdp_2.jpg",
"https://customer-assets.emergentagent.com/job_platinum-barbers/artifacts/e93fheaz_4.jpg",
"https://customer-assets.emergentagent.com/job_platinum-barbers/artifacts/sue3emb0_haircutmain.jpeg"];


// Booking URLs
const BOOK_RIVERVALLEY_URL = "https://www.fresha.com/en-GB/a/platinum-barbers-rivervalley-swords-swords-river-valley-rd-river-valley-road-wvziiy7t/all-offer?menu=true&share=true&entryPoint=venue_choose_location&pId=2829308";
const BOOK_MAINSTREET_URL = "https://www.fresha.com/en-GB/a/platinum-barbers-main-street-swords-dublin-swords-main-street-mypwhdrq/all-offer?menu=true&share=true&entryPoint=venue_choose_location&pId=2829308";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true }
};

// Hero Section
const HeroSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % HERO_BG_IMAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center px-6 py-20">
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentImageIndex}
            src={HERO_BG_IMAGES[currentImageIndex]}
            alt="Platinum Barbers"
            className="w-full h-full object-cover opacity-30 absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          />
        </AnimatePresence>
        <div className="hero-overlay absolute inset-0 !opacity-[60%]"></div>
      </div>
    
    <motion.div
    className="relative z-10 text-center max-w-4xl mx-auto"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}>

      <img
      src={LOGO_URL}
      alt="Platinum Barbers Logo"
      className="w-[400px] md:w-[550px] lg:w-[650px] mx-auto mb-8"
      data-testid="hero-logo" />

      
      <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-white mb-4 tracking-tight">
        PREMIUM MEN'S GROOMING
      </h1>
      
      <p className="text-lg md:text-xl text-platinum-muted mb-12">
        SWORDS, CO.DUBLIN
      </p>
      
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a
        href={BOOK_RIVERVALLEY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-glow bg-platinum-red text-white px-8 py-4 text-lg font-bold uppercase tracking-wider hover:bg-red-700 transition-colors"
        data-testid="book-rivervalley-btn">

          Book Rivervalley
        </a>
        <a
        href={BOOK_MAINSTREET_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-glow bg-platinum-red text-white px-8 py-4 text-lg font-bold uppercase tracking-wider hover:bg-red-700 transition-colors"
        data-testid="book-mainstreet-btn">

          Book Mainstreet Swords
        </a>
      </div>
    </motion.div>
    
    <motion.div
    className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
    animate={{ y: [0, 10, 0] }}
    transition={{ repeat: Infinity, duration: 2 }}>

      <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
        <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
      </div>
    </motion.div>
  </section>
  );
};


// Gallery Section
const GallerySection = () =>
<section className="py-20 px-6 bg-black">
    <div className="max-w-7xl mx-auto">
      <motion.div {...fadeInUp} className="flex items-center justify-center gap-2 mb-8" data-testid="award-badge">
        <Trophy className="w-6 h-6 text-platinum-red" />
        <span className="text-white text-lg sm:text-xl font-bold">Award Winning Barbers</span>
        <Trophy className="w-6 h-6 text-platinum-red" />
      </motion.div>
      
      <motion.div {...fadeInUp} className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">OUR WORK</h2>
        <div className="section-divider w-32 mx-auto"></div>
      </motion.div>
      
      <motion.div
      className="grid grid-cols-2 md:grid-cols-4 gap-4"
      {...staggerContainer}>

        {HAIRCUT_IMAGES.map((img, index) =>
      <motion.div
        key={index}
        className="relative overflow-hidden aspect-square group cursor-pointer"
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.1 }}>

            <img
          src={img}
          alt={`Haircut style ${index + 1}`}
          className="gallery-image w-full h-full object-cover transition-transform duration-300 group-hover:scale-110 group-hover:brightness-110"
          data-testid={`gallery-image-${index + 1}`} />
            <div className="absolute inset-0 bg-platinum-red opacity-0 group-hover:opacity-20 transition-opacity duration-300 pointer-events-none"></div>

          </motion.div>
      )}
      </motion.div>
    </div>
  </section>;


// Locations Section
const LocationsSection = () =>
<section className="py-20 px-6 bg-[#0a0a0a]">
    <div className="max-w-7xl mx-auto">
      <motion.div {...fadeInUp} className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">LOCATIONS</h2>
        <div className="section-divider w-32 mx-auto"></div>
      </motion.div>
      
      <div className="grid md:grid-cols-2 gap-8">
        {/* Rivervalley Location */}
        <motion.div
        className="card-border bg-[#111111] p-6"
        {...fadeInUp}>

          <div className="flex items-center gap-3 mb-4">
            <MapPin className="text-platinum-red w-6 h-6" />
            <h3 className="text-xl font-bold text-white">Platinum Barbers Rivervalley</h3>
          </div>
          <p className="text-platinum-muted mb-4">River Valley Road, Swords, Co. Dublin</p>
          
          <div className="flex items-center gap-3 mb-2">
            <Phone className="text-platinum-red w-5 h-5" />
            <a href="tel:+35318401234" className="text-white hover:text-platinum-red transition-colors">
              +353 1 840 1234
            </a>
          </div>
          <div className="flex items-center gap-3 mb-6">
            <Clock className="text-platinum-red w-5 h-5" />
            <span className="text-platinum-muted">Mon-Sat: 9:00 - 18:00</span>
          </div>
          
          <div className="map-container rounded-lg overflow-hidden" data-testid="map-rivervalley">
            <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2377.8!2d-6.22!3d53.46!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sPlatinum%20Barbers%20Rivervalley!5e0!3m2!1sen!2sie!4v1234567890"
            width="100%"
            height="250"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Platinum Barbers Rivervalley Map">
          </iframe>
          </div>
          
          <a
          href={BOOK_RIVERVALLEY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-glow mt-6 block text-center bg-platinum-red text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-red-700 transition-colors"
          data-testid="location-book-rivervalley">

            Book Now
          </a>
        </motion.div>
        
        {/* Mainstreet Location */}
        <motion.div
        className="card-border bg-[#111111] p-6"
        {...fadeInUp}>

          <div className="flex items-center gap-3 mb-4">
            <MapPin className="text-platinum-red w-6 h-6" />
            <h3 className="text-xl font-bold text-white">Platinum Barbers Mainstreet</h3>
          </div>
          <p className="text-platinum-muted mb-4">Main Street, Swords, Co. Dublin</p>
          
          <div className="flex items-center gap-3 mb-2">
            <Phone className="text-platinum-red w-5 h-5" />
            <a href="tel:+35318405678" className="text-white hover:text-platinum-red transition-colors">
              +353 1 840 5678
            </a>
          </div>
          <div className="flex items-center gap-3 mb-6">
            <Clock className="text-platinum-red w-5 h-5" />
            <span className="text-platinum-muted">Mon-Sat: 9:00 - 18:00</span>
          </div>
          
          <div className="map-container rounded-lg overflow-hidden" data-testid="map-mainstreet">
            <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2377.8!2d-6.22!3d53.46!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sPlatinum%20Barbers%20Mainstreet!5e0!3m2!1sen!2sie!4v1234567890"
            width="100%"
            height="250"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Platinum Barbers Mainstreet Map">
          </iframe>
          </div>
          
          <a
          href={BOOK_MAINSTREET_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-glow mt-6 block text-center bg-platinum-red text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-red-700 transition-colors"
          data-testid="location-book-mainstreet">

            Book Now
          </a>
        </motion.div>
      </div>
    </div>
  </section>;


// Reviews Section
const reviews = [
{
  name: "Michael O'Brien",
  rating: 5,
  text: "Best fade in Swords! The lads really know what they're doing. Been coming here for 2 years now.",
  date: "2 weeks ago"
},
{
  name: "James Murphy",
  rating: 5,
  text: "Always a fresh cut and great chat. Wouldn't go anywhere else. Top class service every time.",
  date: "1 month ago"
},
{
  name: "David Kelly",
  rating: 5,
  text: "Professional, friendly and always deliver exactly what you ask for. Highly recommend!",
  date: "3 weeks ago"
},
{
  name: "Sean Walsh",
  rating: 5,
  text: "Clean shop, skilled barbers, fair prices. What more could you want? 5 stars all day.",
  date: "1 week ago"
}];


const ReviewsSection = () =>
<section className="py-20 px-6 bg-black">
    <div className="max-w-7xl mx-auto">
      <motion.div {...fadeInUp} className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">WHAT THEY SAY</h2>
        <div className="section-divider w-32 mx-auto mb-4"></div>
        <div className="flex items-center justify-center gap-2">
          <span className="text-white font-bold">4.9</span>
          <div className="flex">
            {[...Array(5)].map((_, i) =>
          <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
          )}
          </div>
          <span className="text-platinum-muted">on Google</span>
        </div>
      </motion.div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {reviews.map((review, index) =>
      <motion.div
        key={index}
        className="card-border bg-[#111111] p-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        data-testid={`review-card-${index + 1}`}>

            <div className="flex items-center gap-1 mb-3">
              {[...Array(review.rating)].map((_, i) =>
          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
          )}
            </div>
            <p className="text-white text-sm mb-4">"{review.text}"</p>
            <div className="flex justify-between items-center">
              <span className="text-platinum-red font-bold text-sm">{review.name}</span>
              <span className="text-platinum-muted text-xs">{review.date}</span>
            </div>
          </motion.div>
      )}
      </div>
    </div>
  </section>;


// Footer Section
const FooterSection = () =>
<footer className="py-16 px-6 bg-[#0a0a0a] border-t border-platinum-gray">
    <div className="max-w-7xl mx-auto">
      <div className="grid md:grid-cols-3 gap-12">
        {/* Logo & Socials */}
        <div>
          <img
          src={LOGO_URL}
          alt="Platinum Barbers"
          className="w-48 mb-6"
          data-testid="footer-logo" />

          <p className="text-platinum-muted mb-6">Platinum Barbers men's grooming services in Swords, Dublin. Walk-ins welcome or book online.

        </p>
          <div className="flex gap-4">
            <a
            href="https://instagram.com/platinumbarbers"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white hover:text-platinum-red transition-colors"
            data-testid="social-instagram">

              <Instagram className="w-6 h-6" />
            </a>
            <a
            href="https://facebook.com/platinumbarbers"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white hover:text-platinum-red transition-colors"
            data-testid="social-facebook">

              <Facebook className="w-6 h-6" />
            </a>
          </div>
        </div>
        
        {/* Rivervalley Contact */}
        <div>
          <h4 className="text-white font-bold text-lg mb-4">RIVERVALLEY</h4>
          <address className="not-italic text-platinum-muted">
            <p className="mb-2">River Valley Shopping Centre</p>
            <p className="mb-2">Swords, Co. Dublin</p>
            <a href="tel:+35318401234" className="text-white hover:text-platinum-red transition-colors">+353 1 558 4201

          </a>
          </address>
        </div>
        
        {/* Mainstreet Contact */}
        <div>
          <h4 className="text-white font-bold text-lg mb-4">MAINSTREET SWORDS</h4>
          <address className="not-italic text-platinum-muted">
            <p className="mb-2">Main Street</p>
            <p className="mb-2">Swords, Co. Dublin</p>
            <a href="tel:+35318405678" className="text-white hover:text-platinum-red transition-colors">+353 1 5490549

          </a>
          </address>
        </div>
      </div>
      
      <div className="section-divider w-full my-12"></div>
      
      <div className="text-center text-platinum-muted text-sm">
        <p>&copy; {new Date().getFullYear()} Platinum Barbers. All rights reserved.</p>
      </div>
    </div>
  </footer>;


// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-black">
      <HeroSection />
      <GallerySection />
      <LocationsSection />
      <ReviewsSection />
      <FooterSection />
    </div>);

}

export default App;